import React, { useEffect, useRef, useState } from "react";
import { MapPin, X } from "lucide-react";
import api from "@/lib/api";

// Strip Czech diacritics – the geocoding API returns 403 for accented queries
// (e.g. "Šumperk"), but works for the ASCII form ("Sumperk") and still returns
// the proper accented name in the results.
function stripDiacritics(s) {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function formatPsc(zip) {
  const z = (zip || "").replace(/\s/g, "");
  return z.length === 5 ? `${z.slice(0, 3)} ${z.slice(3)}` : z;
}

// Name search via Open-Meteo (free, CZ-friendly).
async function geocodeName(query) {
  if (!query || query.length < 2) return [];
  try {
    const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
      stripDiacritics(query)
    )}&count=8&language=cs&countryCode=CZ`;
    const r = await fetch(url);
    const d = await r.json();
    return (d?.results || []).map((s) => {
      const label = s.admin1 && s.name !== s.admin1 ? `${s.name}, ${s.admin1}` : s.name;
      return { key: `n-${s.id}`, label, value: label };
    });
  } catch (_) {
    return [];
  }
}

// PSČ search via our bundled CZ postal-code dataset (offline, reliable).
async function geocodePsc(digits) {
  try {
    const { data } = await api.get("/geo/psc", { params: { code: digits } });
    return (data?.items || []).map((r, i) => {
      const psc = formatPsc(r.zipcode);
      return {
        key: `p-${r.zipcode}-${i}`,
        label: `${r.name} (${psc})`,
        value: `${r.name}, ${psc}`,
      };
    });
  } catch (_) {
    return [];
  }
}

export default function LocationAutocomplete({
  value,
  onChange,
  required = false,
  placeholder = "Město, obec nebo PSČ",
  testId = "location-input",
}) {
  const [text, setText] = useState(value || "");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);

  const runSearch = (raw) => {
    clearTimeout(debRef.current);
    const trimmed = (raw || "").trim();
    if (trimmed.length < 2) { setItems([]); return; }
    const digits = trimmed.replace(/\s/g, "");
    const isPsc = /^\d{2,5}$/.test(digits);
    debRef.current = setTimeout(async () => {
      setItems(isPsc ? await geocodePsc(digits) : await geocodeName(trimmed));
    }, 220);
  };

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (s) => {
    setText(s.value);
    onChange(s.value);
    setOpen(false);
    setActive(-1);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(items[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] z-10 pointer-events-none" size={14} />
      <input
        type="text"
        className="stb-input pl-9 pr-9"
        placeholder={placeholder}
        value={text}
        required={required}
        onChange={(e) => { const v = e.target.value; setText(v); onChange(v); setOpen(true); setActive(-1); runSearch(v); }}
        onFocus={() => { setOpen(true); if (text) runSearch(text); }}
        onKeyDown={onKey}
        autoComplete="off"
        data-testid={testId}
      />
      {text && (
        <button
          type="button"
          aria-label="Vymazat"
          onClick={() => { setText(""); onChange(""); setItems([]); setActive(-1); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-[#FF6B00] z-10"
        >
          <X size={14} />
        </button>
      )}
      {open && items.length > 0 && (
        <ul
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-[#0E0E0E] border-2 border-[#262626] max-h-64 overflow-y-auto shadow-2xl"
          data-testid="location-suggestions"
        >
          {items.map((s, i) => (
            <li key={s.key}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                  i === active ? "bg-[#1a1a1a]" : "hover:bg-[#1a1a1a]"
                }`}
                data-testid={`location-suggestion-${i}`}
              >
                <MapPin size={12} className="text-[#FF6B00]" />
                <span className="text-white text-sm">{s.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
